/**
 * <g-card> - Kanban Card
 *
 * A draggable card for use inside g-column.
 * Supports any HTML content as children.
 *
 * Inside a <g-kanban>, a card can be pinned: an empty pin shows in its top
 * right corner on hover, and a click on it pins the card — a filled pin and a
 * light tint, kept in this browser (localStorage) per page and board. A pin
 * does not move the card or change the record. The card's title stays on one
 * line with an ellipsis, clear of the pin, and a cut title shows in full as
 * its tooltip. Mark it `class="g-card-title"` on a hand-written card.
 *
 * @example
 * <g-card card-id="task-123">
 *   <strong>Task Title</strong>
 *   <p>Description</p>
 * </g-card>
 *
 * @attr {string} card-id - Unique identifier (required for events and pins)
 * @attr {boolean} pinned - Set by the card itself when the viewer has pinned it
 *
 * @fires card-pinned - { cardId, pinned } when the viewer pins or unpins the card
 */
import { pins } from './pins.js';

const PIN_PATH = 'M12 17v5M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z';

// The pin is invisible until the card is hovered (or the pin focused), and
// always shown on a pinned card. Touch screens have no hover, so there it
// stays faintly visible. The tint is mixed into the card's own surface.
//
// The card's title is one line, ellipsised, and stops short of the pin. The
// title is `.g-card-title` on a hand-written card, and the first bold line of
// the body (`.p-1 > .font-bold:first-child`) on a generated one.
const TITLE_SELECTOR = '.g-card-title, :scope > .p-1:first-child > .font-bold:first-child';

function ensurePinStyles() {
  if (typeof document === 'undefined' || document.getElementById('g-card-pin-styles')) return;
  const style = document.createElement('style');
  style.id = 'g-card-pin-styles';
  style.textContent = `
g-card:has(> .g-pin){position:relative}
g-card:has(> .g-pin) .g-card-title,g-card:has(> .g-pin) > .p-1:first-child > .font-bold:first-child{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;padding-right:1.75rem}
g-card > .g-pin{position:absolute;top:.375rem;right:.375rem;width:1.75rem;height:1.75rem;display:flex;align-items:center;justify-content:center;border-radius:9999px;color:color-mix(in oklab,var(--color-base-content) 55%,transparent);background:transparent;opacity:0;transition:opacity .15s,color .15s,background-color .15s;cursor:pointer;border:0;padding:0}
g-card > .g-pin:hover{color:var(--color-base-content);background-color:color-mix(in oklab,var(--color-base-content) 8%,transparent)}
g-card:hover > .g-pin,g-card > .g-pin:focus-visible,g-card[pinned] > .g-pin{opacity:1}
g-card[pinned] > .g-pin{color:var(--color-primary)}
g-card[pinned] > .g-pin svg{fill:currentColor}
g-card[pinned]{background-color:color-mix(in oklab,var(--color-primary) 7%,var(--color-base-100));border-color:color-mix(in oklab,var(--color-primary) 35%,var(--color-base-300))}
@media (hover:none){g-card > .g-pin{opacity:.45}}
@media (prefers-reduced-motion:reduce){g-card > .g-pin{transition:none}}`;
  (document.head || document.documentElement).appendChild(style);
}

export class GCard extends HTMLElement {
  static get observedAttributes() {
    return ['card-id'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
    this.syncPin();
  }

  // Alpine binds :card-id after the card is in the page.
  attributeChangedCallback() {
    if (this.isConnected) this.syncPin();
  }

  render() {
    // Use Tailwind classes for all styling
    this.classList.add(
      'relative', 'block', 'rounded-lg', 'p-3.5', 'mb-2', 'cursor-grab',
      'bg-base-100', 'border', 'border-base-300',
      'shadow-sm', 'transition-all', 'duration-150',
      'hover:shadow-md', 'hover:-translate-y-0.5',
      'overflow-hidden'
    );
  }

  // The board this card's pins belong to, or null where pins are off.
  get pinKey() {
    const board = this.closest('g-kanban');
    if (!board || board.hasAttribute('no-pins')) return null;
    return board.getAttribute('pin-key') || board.id || 'board';
  }

  syncPin() {
    const key = this.pinKey;
    const id = this.getAttribute('card-id');
    let btn = this.querySelector(':scope > .g-pin');
    if (!key || !id) {
      btn?.remove();
      this.removeAttribute('pinned');
      return;
    }
    if (!btn) {
      ensurePinStyles();
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'g-pin';
      btn.innerHTML = `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${PIN_PATH}"/></svg>`;
      // Its own click only: not the card's (which opens the record), not a drag.
      btn.addEventListener('pointerdown', (e) => e.stopPropagation());
      btn.addEventListener('mousedown', (e) => e.stopPropagation());
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.togglePin();
      });
      this.appendChild(btn);
      this.addEventListener('pointerenter', () => this.syncTitleTooltip());
    }
    this.setPinned(pins.has(key, id));
  }

  // A title cut short by the ellipsis shows in full as its tooltip. Read on
  // hover: the text is bound by the host and changes under the card.
  syncTitleTooltip() {
    const title = this.querySelector(TITLE_SELECTOR);
    if (!title) return;
    const text = title.textContent.trim();
    if (title.scrollWidth > title.clientWidth && text) {
      title.title = text;
      title.dataset.gTooltip = '1';
    } else if (title.dataset.gTooltip) {
      title.removeAttribute('title');
      delete title.dataset.gTooltip;
    }
  }

  setPinned(pinned) {
    this.toggleAttribute('pinned', pinned);
    const btn = this.querySelector(':scope > .g-pin');
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(pinned));
    btn.setAttribute('aria-label', pinned ? 'Unpin card' : 'Pin card');
    btn.title = pinned ? 'Unpin' : 'Pin';
  }

  togglePin() {
    const key = this.pinKey;
    const id = this.getAttribute('card-id');
    if (!key || !id) return;
    const pinned = pins.toggle(key, id);
    // The same record on another board of this page with the same pins.
    for (const card of document.querySelectorAll('g-card')) {
      if (card.getAttribute('card-id') === id && card.pinKey === key) card.setPinned(pinned);
    }
    this.dispatchEvent(new CustomEvent('card-pinned', {
      detail: { cardId: id, pinned },
      bubbles: true,
      composed: true,
    }));
  }
}

if (!customElements.get('g-card')) {
  customElements.define('g-card', GCard);
}
